import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  fromEvent,
  merge,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throttleTime,
} from 'rxjs';
import { BrowserTools, SolBannerHandler } from '@sol/components';
import { PlatformBannerDisplayItem } from '../models/platform-banner';
import { AsmComponentsService } from '@asm/components-core/services/asm-components.service';
import { PlatformBannerService } from '../services/platform-banner.service';
import { SessionService } from '@asm/components-platform-context/services/session.service';
import { AsmBannerTools } from '../tools/asm-banner.tools';
import { AsmContextTools } from '@asm/components-platform-context/tools/asm-context-tools';

/**
 * The banners an ordinary reader sees, on every page of the app that should show them.
 *
 * It draws nothing. The app-shell `sol-banner-host` renders wherever it is mounted, so *what is
 * published* is the only thing keeping banners out of the app that should not have them — which is
 * why the app shell holds one of these and no page does.
 *
 * Construct it as a field of the shell, the shape `SolBannerHandler` and `SolUrlHandler` set: it
 * captures the shell's `DestroyRef` from the constructor's injection context, so its slice of the
 * surface and every listener go with the app. Each read runs inside that same subscription, so one
 * still in flight when the shell is torn down is cancelled rather than landing afterwards, and each
 * trigger cancels the read before it so the newest answer is the one that lands.
 *
 * The behaviour it implements is stated in `banner-display.md` — when the strip re-reads and what a
 * failed read shows. It neither caps nor re-orders: with the strip paging through the set, every banner
 * aimed at a reader is reachable, and the order is the producing side's. It states no placement, so the
 * surface keeps its own `inline` default and the app shifts down by the strip rather than being covered.
 * A reader cannot remove a banner in this version, so nothing here records what anyone closed.
 *
 * Only the platform app is read for, by its active scope. The admin app carries the authoring page and
 * no strip, and the partner app cannot be read for at all: the display endpoint takes a scope and
 * nothing else, so a partner-app caller has no way to say which partner it is — a gap `contract.md`
 * names rather than invents, with question 20. A reader with no scope yet is read for nothing rather
 * than for the scope they arrived from. The embedded shell holds one too — it is the platform app
 * running inside the Vue host.
 *
 * One detail that document belongs to but the code cannot state on its own: a browser reports a tab
 * returning to the foreground as both a visibility change and a window focus, so the two are
 * coalesced into a single read rather than fetching twice on the commonest path. Only a top-level
 * window is asked: an iframe is also focused by a click arriving from its host, which is not a tab
 * returning, and the visibility change already follows the top-level tab.
 */
export class AsmBannerStripHandler {
  private static readonly OWNER = 'asm-platform-banners';

  private static readonly REFOCUS_COALESCE_MS = 100;

  // Dependencies
  private readonly asmComponentsService = inject(AsmComponentsService);
  private readonly platformBannerService = inject(PlatformBannerService);
  private readonly sessionService = inject(SessionService);
  private readonly document = inject(DOCUMENT);

  // Handlers
  private readonly solBanners: SolBannerHandler;

  private published: readonly PlatformBannerDisplayItem[] = [];
  private publishedScope = '';

  constructor() {
    const destroyRef = inject(DestroyRef);
    this.solBanners = new SolBannerHandler({
      owner: AsmBannerStripHandler.OWNER,
      actionPressed: key => this.onActionPressed(key),
    });
    merge(this.loadAndContextChanges(), this.tabRegainedFocus())
      .pipe(
        tap(() => this.emptyStripWhenContextChanged()),
        switchMap(() => this.read()),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe(items => this.publish(items));
  }

  private loadAndContextChanges(): Observable<unknown> {
    const scope$ = new BehaviorSubject(this.asmComponentsService.state.scope);
    this.asmComponentsService.watchComputed(
      state => state.scope,
      scope => scope$.next(scope),
    );
    const appId$ = new BehaviorSubject(this.sessionService.state.appId);
    this.sessionService.watchComputed(
      state => state.appId,
      appId => appId$.next(appId),
    );
    return combineLatest([scope$.pipe(distinctUntilChanged()), appId$.pipe(distinctUntilChanged())]);
  }

  private tabRegainedFocus(): Observable<unknown> {
    const view = this.document.defaultView;
    const windowFocusMeansTabFocus = view !== null && !BrowserTools.isEmbedded();
    return merge(
      fromEvent(this.document, 'visibilitychange').pipe(filter(() => this.document.visibilityState === 'visible')),
      windowFocusMeansTabFocus ? fromEvent(view, 'focus') : EMPTY,
    ).pipe(throttleTime(AsmBannerStripHandler.REFOCUS_COALESCE_MS));
  }

  private read(): Observable<readonly PlatformBannerDisplayItem[]> {
    const scope = this.publishableScope();
    if (scope === undefined) {
      return of([]);
    }
    return this.platformBannerService.fetchDisplayBanners(scope).pipe(
      take(1),
      catchError(() => EMPTY),
    );
  }

  private publishableScope(): string | undefined {
    if (!AsmContextTools.isPlatformContext(this.sessionService.state)) {
      return undefined;
    }
    const scope = this.asmComponentsService.state.scope;
    return scope === undefined || scope === '' ? undefined : scope;
  }

  private emptyStripWhenContextChanged(): void {
    const scope = this.publishableScope() ?? '';
    if (scope === this.publishedScope) {
      return;
    }
    this.publishedScope = scope;
    this.publish([]);
  }

  private publish(items: readonly PlatformBannerDisplayItem[]): void {
    this.published = items;
    if (this.published.length === 0) {
      this.solBanners.clear();
      return;
    }
    this.solBanners.publish(AsmBannerTools.toStripConfigs(this.published));
  }

  private onActionPressed(key: string): void {
    const href = AsmBannerTools.linkHref(this.published.find(item => String(item.id) === key)?.linkUrl);
    if (href === undefined) {
      return;
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  }
}
